import type { PageSchema } from "../blocks/types";

/**
 * `/pt/blog/quanto-custa-um-casamento-em-portugal/` — BTQ-47, Этап 2, волна A.
 *
 * Спецификация: `btq.pt/pt-core/pt-specs-v2/03-money-post-quanto-custa.md`.
 * Драфт: `btq.pt/pt-core/pt-pages-v2/01-quanto-custa-um-casamento.md`.
 *
 * PT-only страница: английского двойника у неё нет и не должно быть. Запрос
 * «quanto custa um casamento em portugal» коммерческий и целиком португальский;
 * на английской стороне ему соответствия нет.
 *
 * Все суммы сверены с коридором `PT-AI-SEO-MASTER-2026-08.md` §1.4. Ошибка v1
 * (аренда «3 000 – 30 000 €», выше рынка втрое) здесь исправлена на 500–2 500 €
 * paredes nuas и 60–120 €/pessoa с кейтерингом.
 */

export const moneyCasamento: PageSchema = {
  en: { kind: "component" },
  pt: {
    kind: "blocks",
    meta: {
      title: "Quanto Custa um Casamento em Portugal em 2026: Valores Reais por Pessoa",
      description:
        "Um casamento para 100 convidados custa entre 15 000 € e 25 000 €. Valor por pessoa, as rubricas do orçamento, o custo do registo civil (120 € a 200 €) e o que faz o total subir.",
      headQuery: "quanto custa um casamento em portugal",
    },
    blocks: [
      {
        type: "hero",
        eyebrow: "Orçamento",
        h1: "Quanto custa um casamento em Portugal",
        image: "services-ready/svc-planning-couple-floorplan-palace.webp",
        imageAlt: "Planta de sala de um palácio sobre a mesa durante o desenho do orçamento",
      },
      {
        type: "answerFirst",
        paragraphs: [
          "Um casamento em Portugal para 100 convidados custa, em média, entre 15 000 € e 25 000 €. O valor por convidado — espaço e catering juntos — situa-se entre 60 € e 130 €, e é a rubrica que mais pesa no total.",
          "A partir daí o número move-se em duas direcções: para baixo, com uma lista curta e uma data fora de época; para cima, com convidados a chegar de vários países e mais do que um dia de programa. As tabelas abaixo mostram por onde passa cada euro.",
        ],
      },
      {
        type: "table",
        heading: "Quanto custa por tipo de casamento",
        columns: ["Tipo", "Convidados", "Custo estimado"],
        rows: [
          ["Micro-wedding, íntimo", "20 a 40", "5 000 € a 10 000 €"],
          ["Média nacional", "cerca de 100", "15 000 € a 25 000 €"],
          ["Grande, com produção", "150 ou mais", "35 000 € a 50 000 €+"],
          ["Destination, 2 a 4 dias", "50 a 250", "a partir de 70 000 €"],
        ],
        note:
          "A última linha é a nossa, e é a única que não aparece em nenhum comparador. Está explicada mais abaixo, rubrica a rubrica: um casamento de vários dias com convidados de vários países não é o mesmo projeto que uma noite única, e a diferença não está no luxo, está na quantidade de operação.",
      },
      {
        type: "table",
        heading: "As rubricas de um orçamento",
        columns: ["Rubrica", "Intervalo", "Nota"],
        rows: [
          [
            "Espaço e catering (copo-d'água)",
            "60 € a 130 € por pessoa",
            "A maior fatia do orçamento, e a que cresce com cada convidado",
          ],
          [
            "Registo civil",
            "120 € ou 200 €",
            "120 € em dia útil na conservatória, 200 € fora dela, ao fim de semana ou em feriado",
          ],
          ["Fotografia e vídeo", "1 000 € a 3 000 €", "Reserva-se com cerca de um ano de antecedência"],
          ["Animação e DJ", "500 € a 1 200 €", "Banda ao vivo sobe este intervalo"],
          [
            "Decoração e flores",
            "1 000 € a 2 500 €",
            "A rubrica mais elástica: a mesma ideia executa-se em várias escalas",
          ],
          ["Vestuário dos noivos", "1 000 € a 3 000 €", "Provas e ajustes incluídos"],
        ],
      },
      {
        type: "prose",
        heading: "O valor por pessoa, e o que ele esconde",
        paragraphs: [
          "O mercado português conversa em valor por pessoa, e faz sentido: é assim que o catering é cobrado e é o número que permite comparar duas propostas. O problema aparece quando esse valor é lido como o custo do casamento, porque cobre apenas a comida, a bebida e o serviço à mesa.",
          "Fora dele ficam o aluguer do espaço, a produção técnica, as flores, a fotografia, a música e a logística dos convidados. Um valor por pessoa baixo com um espaço vazio pode acabar mais caro do que um valor por pessoa mais alto numa quinta que já inclui cozinha, mobiliário e equipa. A comparação só é justa quando se compara o total entregue, e não a primeira página da proposta.",
          "É por isso que apresentamos sempre o orçamento rubrica a rubrica, com o que está incluído e o que fica em aberto até à contagem final de convidados.",
        ],
      },
      {
        type: "prose",
        heading: "O que faz o total subir",
        paragraphs: [
          "O número de convidados é a variável que multiplica todas as outras. Passar de 80 para 120 convidados não acrescenta metade do catering: acrescenta também mesas, louça, pessoal, transporte e, muitas vezes, um espaço diferente.",
          "O dia da semana pesa mais do que parece. Um sábado em época alta, de maio a outubro, é o pico da procura. A mesma quinta, numa sexta-feira ou num domingo, costuma pedir uma quota mínima de convidados mais baixa ou um valor por pessoa mais baixo. A diferença entre um sábado de agosto e uma quinta-feira de abril é das poucas decisões que baixam o total sem mexer em nada do que se vê.",
          "A região também decide. A linha de Cascais e Sintra concentra procura internacional e preços mais altos, enquanto o interior do Alentejo e o norte oferecem espaços comparáveis por menos, com a distância a entrar no orçamento sob a forma de transporte e alojamento.",
          "O formato é a última variável grande. Uma noite única e um fim de semana de três dias são projetos diferentes: o segundo acrescenta jantar de véspera, brunch e um programa para os convidados, e é o formato natural quando a maioria vem de fora.",
          "Por fim, os custos que aparecem depois: horas para lá do limite contratado, fornecedores obrigatórios da lista da quinta, quota mínima de convidados ao sábado e IVA somado à parte. Nenhum destes aparece na primeira página de uma proposta, e todos aparecem no total.",
        ],
      },
      {
        type: "procedural",
        heading: "Casar no civil: quanto custa mesmo",
        intro:
          "O ato civil e a festa são contas diferentes, e somá-las é a confusão mais comum. Estes são os valores do registo:",
        items: [
          "120 € — processo e registo de casamento, com a celebração na conservatória dentro do horário de funcionamento",
          "200 € — quando a celebração é fora da conservatória, nesta mas fora do horário de funcionamento, ou ao sábado, domingo ou feriado; este valor substitui os 120 €",
          "+100 € — convenção antenupcial em regime típico",
          "+160 € — convenção em regime atípico",
          "+30 € — registo da convenção celebrada perante entidade diferente da conservatória, valor que acresce ao preço dessa entidade",
          "O valor de 200 € aplica-se com o transporte do conservador assegurado pelos noivos ou mediante acordo sobre essas despesas",
          "Há isenção de emolumentos para quem prove insuficiência económica, mediante documento da autoridade administrativa competente apresentado na conservatória",
        ],
        note:
          "O processo preliminar abre-se em qualquer conservatória ou pelo portal Civil Online, e o casamento deve celebrar-se nos seis meses seguintes ao despacho favorável. Muitos casais tratam do civil num dia útil e fazem depois uma cerimónia simbólica no espaço escolhido, o que dá liberdade total de local e de hora.",
      },
      {
        type: "prose",
        heading: "Onde se situam os casamentos que produzimos",
        paragraphs: [
          "As produções da Event Boutique começam nos 70 000 €, e a maioria dos casais investe entre 90 000 € e 250 000 €. Esse valor é o do casamento inteiro, não do nosso serviço: inclui espaço, catering, produção, design, entretenimento, fotografia e a logística dos convidados. São doze a quinze celebrações por ano, com cinquenta a duzentos e cinquenta convidados e dois a quatro dias de programa.",
          "O intervalo está acima da média nacional de 15 000 € a 25 000 €, e a diferença não está no preço das mesmas coisas. Está na quantidade de coisas: dois a quatro serviços de refeição em vez de um, produção técnica levada para propriedades sem infraestrutura, alojamento e transferes de um grupo internacional em vários dias, equipa alojada no local desde a véspera em vez de uma coordenação de doze horas, e o mesmo espaço em exclusividade durante três dias.",
          "Um casamento em Portugal pode custar bastante menos, e a maioria custa. Se o vosso caso é uma lista curta, numa quinta com cozinha própria, num só dia e com convidados do país, uma equipa mais pequena serve-vos melhor, e preferimos dizê-lo na primeira conversa.",
        ],
      },
      {
        type: "faq",
        heading: "Perguntas frequentes",
        items: [
          {
            q: "Quanto custa um casamento em Portugal?",
            a: "Para 100 convidados, entre 15 000 € e 25 000 € na média nacional. O valor por convidado, com espaço e catering juntos, fica entre 60 € e 130 €. Um micro-wedding de 20 a 40 pessoas fica entre 5 000 € e 10 000 €, e um casamento de 150 ou mais convidados com produção completa parte dos 35 000 €.",
          },
          {
            q: "Quanto custa um casamento para 100 pessoas?",
            a: "Entre 15 000 € e 25 000 € num formato de uma noite, com espaço, catering, fotografia, música e decoração. O que move este número dentro do intervalo é o dia da semana, a região e se a quinta tem cozinha própria ou obriga a catering externo.",
          },
          {
            q: "Qual é a rubrica mais cara de um casamento?",
            a: "Na maioria dos orçamentos, o catering, porque se multiplica por cada convidado. O espaço parece a decisão maior porque é a primeira e a mais visível, mas em muitos casamentos custa menos do que a comida e as bebidas servidas nele. Depois destes dois vem a produção técnica, que sobe muito em espaços ao ar livre onde tudo tem de ser levado para o local.",
          },
          {
            q: "Quanto custa casar pelo civil em Portugal?",
            a: "O processo e o registo de casamento custam 120 €. São 200 € quando a celebração acontece fora da conservatória, nesta mas fora do horário de funcionamento, ou ao sábado, domingo ou feriado — valor que substitui os 120 €, não acresce. A convenção antenupcial acrescenta 100 € em regime típico, 160 € em regime atípico e 30 € pelo registo da convenção celebrada perante entidade diferente da conservatória. O valor de 200 € aplica-se com o transporte do conservador assegurado pelos noivos ou mediante acordo sobre essas despesas, e há isenção de emolumentos para quem prove insuficiência económica perante a conservatória.",
          },
          {
            q: "Quanto custa alugar uma quinta para casamento?",
            a: "Entre 60 € e 120 € por pessoa quando o catering está incluído. Em regime de paredes nuas — apenas o espaço, sem cozinha nem equipa — os valores vão de 500 € a 2 500 €, e a esse número somam-se catering, louça, pessoal e por vezes gerador.",
          },
          {
            q: "Quanto custa um wedding planner em Portugal?",
            a: "Depende do modelo. Coordenação apenas do dia: 450 € a 1 150 €. Planeamento parcial: 1 200 € a 2 500 €. Planeamento integral: 3 000 € a 6 000 €. Casamentos destination de vários dias: a partir de 5 000 € e até 12 000 € ou mais. Há também agências que trabalham a 10% a 15% do orçamento total.",
          },
          {
            q: "É possível casar em Portugal com um orçamento pequeno?",
            a: "Sim, e acontece todos os fins de semana. Uma lista curta, uma data fora de época, uma quinta com cozinha própria e um formato de um só dia reduzem o total de forma significativa. Nesse caso, o que faz sentido é uma equipa mais pequena do que a nossa: dizemos isso na primeira conversa, porque a Event Boutique está dimensionada para casamentos com produção completa e convidados internacionais.",
          },
          {
            q: "Vale a pena contratar quem organize, se o orçamento é limitado?",
            a: "Depende de onde está a pressão. Se o casamento é numa quinta com cozinha própria, com convidados quase todos do país e num só dia, uma coordenação apenas no dia costuma ser suficiente. Se há vários fornecedores independentes, convidados que chegam de avião ou um espaço sem infraestrutura, a produção deixa de ser um luxo e passa a ser o que evita que a família passe o dia a resolver problemas.",
          },
          {
            q: "Quem paga o quê num casamento?",
            a: "Em Portugal não há hoje uma regra fixa, e a divisão mais comum é entre os noivos e as duas famílias, decidida no início e por rubricas inteiras — uma família assume o catering, a outra o espaço, os noivos a fotografia e a música, por exemplo. Dividir por rubricas em vez de por percentagens evita a conversa mais desconfortável do processo, que é a de acertar contas no fim.",
          },
        ],
      },
      {
        type: "related",
        heading: "A seguir",
        links: [
          { routeKey: "venues", label: "Onde escolher o espaço: quintas para casamentos" },
          { routeKey: "service-wedding-planning", label: "Como organizar um casamento, passo a passo" },
        ],
      },
      {
        type: "cta",
        heading: "Um enquadramento para o vosso caso",
        body:
          "Com a data, o número de convidados e a região conseguimos dizer o que é possível e em que intervalo se situa, antes de qualquer proposta formal.",
        routeKey: "contact",
        label: "Falar connosco",
      },
    ],
  },
};
